import useLocale from 'next-translate/useTranslation';
import { FC } from 'react';

import {
  AspectRatioType,
  DurationType,
  getDefaultResolutionForModel,
  ResolutionType,
  resolutionLabels,
  videoModelsWithDescription,
  VideoModelType,
} from '@/constants/videoModels';

interface VideoOptionsProps {
  selectedModel: VideoModelType;
  duration: DurationType;
  aspectRatio: AspectRatioType;
  resolution?: ResolutionType;
  generateAudio?: boolean;
  onDurationChange: (duration: DurationType) => void;
  onAspectRatioChange: (aspectRatio: AspectRatioType) => void;
  onResolutionChange?: (resolution: ResolutionType) => void;
  onGenerateAudioChange?: (generateAudio: boolean) => void;
  hasImage?: boolean;
}

export const VideoOptions: FC<VideoOptionsProps> = ({
  selectedModel,
  duration,
  aspectRatio,
  resolution,
  generateAudio,
  onDurationChange,
  onAspectRatioChange,
  onResolutionChange,
  onGenerateAudioChange,
  hasImage = false,
}) => {
  const { t } = useLocale('ai-images');

  const model = videoModelsWithDescription.find(
    (m) => m.title === selectedModel,
  );
  if (!model) return null;

  // Show duration if model has duration options
  const showDuration =
    model.options.duration && model.options.duration.length > 0;
  // Show aspect ratio if model has multiple aspect ratios and either:
  // 1. No image is uploaded, OR
  // 2. Model supports image-to-video (can use images as input)
  const supportsImageInput = model.options.inputType?.includes('image');
  const showAspectRatio =
    model.options.aspectRatio.length > 1 && (!hasImage || supportsImageInput);
  // Show resolution if model has resolution options
  const showResolution =
    model.options.resolution && model.options.resolution.length > 1;
  // Show audio toggle for all models
  const showAudioToggle = true;
  // Show discount badge only when model has an explicit audio discount
  const audioMultiplier = model.options.audioDisabledCreditsMultiplier;
  const hasAudioDiscount = audioMultiplier !== undefined && audioMultiplier < 1;
  const discountPercent = hasAudioDiscount
    ? Math.round((1 - audioMultiplier!) * 100)
    : 0;

  if (!showDuration && !showAspectRatio && !showResolution && !showAudioToggle)
    return null;

  // Calculate grid columns based on visible dropdown options (audio toggle is separate)
  const dropdownCount =
    (showDuration ? 1 : 0) +
    (showAspectRatio ? 1 : 0) +
    (showResolution ? 1 : 0);
  const gridClass =
    dropdownCount === 3
      ? 'grid-cols-1 sm:grid-cols-3'
      : dropdownCount === 2
        ? 'grid-cols-1 sm:grid-cols-2'
        : 'grid-cols-1';

  return (
    <div className={`grid ${gridClass} gap-4`}>
      {showDuration && (
        <div>
          <label
            htmlFor='duration'
            className='block text-sm font-medium text-gray-700 dark:text-slate-400'
          >
            {t('Duration')}
          </label>
          <select
            id='duration'
            name='duration'
            value={duration}
            onChange={(e) => onDurationChange(e.target.value as DurationType)}
            className='mt-1 block w-full rounded-md border-gray-300 py-2 pl-3 pr-10 text-base focus:border-primary-500 focus:outline-none focus:ring-primary-500 dark:border-white/5 dark:bg-slate-700/50 sm:text-sm'
          >
            {model.options.duration?.map((d) => (
              <option key={d} value={d}>
                {d} {t('seconds')}
              </option>
            ))}
          </select>
        </div>
      )}

      {showAspectRatio && (
        <div>
          <label
            htmlFor='aspectRatio'
            className='block text-sm font-medium text-gray-700 dark:text-slate-400'
          >
            {t('Aspect Ratio')}
          </label>
          <select
            id='aspectRatio'
            name='aspectRatio'
            value={aspectRatio}
            onChange={(e) =>
              onAspectRatioChange(e.target.value as AspectRatioType)
            }
            className='mt-1 block w-full rounded-md border-gray-300 py-2 pl-3 pr-10 text-base focus:border-primary-500 focus:outline-none focus:ring-primary-500 dark:border-white/5 dark:bg-slate-700/50 sm:text-sm'
          >
            {model.options.aspectRatio.map((ar) => (
              <option key={ar} value={ar}>
                {ar}
              </option>
            ))}
          </select>
        </div>
      )}

      {showResolution && onResolutionChange && (
        <div>
          <label
            htmlFor='resolution'
            className='block text-sm font-medium text-gray-700 dark:text-slate-400'
          >
            {t('Resolution')}
          </label>
          <select
            id='resolution'
            name='resolution'
            value={resolution || getDefaultResolutionForModel(model)}
            onChange={(e) =>
              onResolutionChange(e.target.value as ResolutionType)
            }
            className='mt-1 block w-full rounded-md border-gray-300 py-2 pl-3 pr-10 text-base focus:border-primary-500 focus:outline-none focus:ring-primary-500 dark:border-white/5 dark:bg-slate-700/50 sm:text-sm'
          >
            {model.options.resolution!.map((res) => {
              const multiplier =
                model.options.resolutionCreditsMultiplier?.[res];
              const suffix =
                multiplier && multiplier > 1
                  ? ` (${multiplier}${t('× credits')})`
                  : '';
              return (
                <option key={res} value={res}>
                  {t(resolutionLabels[res]) || res}
                  {suffix}
                </option>
              );
            })}
          </select>
        </div>
      )}

      {showAudioToggle && onGenerateAudioChange && (
        <div className={dropdownCount > 0 ? 'col-span-full' : ''}>
          <div className='flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 dark:border-white/10 dark:bg-slate-800/50'>
            <div className='flex items-center gap-2'>
              <svg
                className='h-4 w-4 text-gray-500 dark:text-gray-400'
                fill='none'
                viewBox='0 0 24 24'
                strokeWidth='1.5'
                stroke='currentColor'
              >
                {generateAudio !== false ? (
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    d='M19.114 5.636a9 9 0 010 12.728M16.463 8.288a5.25 5.25 0 010 7.424M6.75 8.25l4.72-4.72a.75.75 0 011.28.53v15.88a.75.75 0 01-1.28.53l-4.72-4.72H4.51c-.88 0-1.704-.507-1.938-1.354A9.01 9.01 0 012.25 12c0-.83.112-1.633.322-2.396C2.806 8.756 3.63 8.25 4.51 8.25H6.75z'
                  />
                ) : (
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    d='M17.25 9.75L19.5 12m0 0l2.25 2.25M19.5 12l2.25-2.25M19.5 12l-2.25 2.25m-10.5-6l4.72-4.72a.75.75 0 011.28.531V19.94a.75.75 0 01-1.28.53l-4.72-4.72H4.51c-.88 0-1.704-.507-1.938-1.354A9.01 9.01 0 012.25 12c0-.83.112-1.633.322-2.396C2.806 8.756 3.63 8.25 4.51 8.25H6.75z'
                  />
                )}
              </svg>
              <span className='text-sm font-medium text-gray-700 dark:text-slate-300'>
                {t('Generate audio')}
              </span>
              {generateAudio === false && hasAudioDiscount && (
                <span className='rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400'>
                  {discountPercent}% off
                </span>
              )}
            </div>
            <button
              type='button'
              role='switch'
              aria-checked={generateAudio !== false}
              onClick={() => onGenerateAudioChange(generateAudio === false)}
              className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-slate-900 ${
                generateAudio !== false
                  ? 'bg-primary-600'
                  : 'bg-gray-300 dark:bg-gray-600'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  generateAudio !== false ? 'translate-x-4' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
